import { DadosRelatorioCaqDto } from "../dtos/caq-por-etapa-dto";

export interface CategoriaFiltro {
  /**
   *  1=Creche, 2=Pré-escola, 3=EF Anos Iniciais, 4=EF Anos Finais, 5=Ensino Médio, 6=EJA
   */
  etapa: number,
  /**
   * Parcial ou integral
   */
  ehParcial: boolean,

  /**
   * Filtro de localidade.
   */
  localidade: {
     filtro: 'NENHUM' | 'URBANO' | 'CAMPO' | 'TERRA_INDIGENA' | 'QUILOMBOLA' | string,
     casoFiltroNenhum: 'URBANO_SEM_FILTRO' | 'URBANO' | 'RURAL' | 'CAMPO' | 'TERRA_INDIGENA' | 'QUILOMBOLA' //rural só aparece sem filtros!
  }
}

export class CaqUtils {
  /**
  * Retorna, de forma conceitual, o "nome do bloco" dentro do `DadosRelatorioCaqDto`
  *
  * O valor retornado é o nome da propriedade que agrupa os dados e que contém, dentro dela,
  * campos como `numeroEscolas` e `numeroMatriculas`.
  *
  * 1) Prefixo pela etapa:
  *    - 1 -> "creche"
  *    - 2 -> "preEscola"
  *    - 3 -> "ensFunAnosIniciais"
  *    - 4 -> "ensFunAnosFinais"
  *    - 5 -> "ensMedio"
  *    - 6 -> "eja"
  *
  * 2) Sufixo pela localidade:
  *    - "URBANO"        -> "Urbana"
  *    - "RURAL"         -> "Rural"
  *    - "CAMPO"         -> "Campo"
  *    - "TERRA_INDIGENA"-> "TerraIndigena"
  *    - "QUILOMBOLA"    -> "Quilombola"
  *
  * 3) Sufixo pelo turno:
  *    - Se etapa = 6 (EJA): sempre "Parcial"
  *    - Caso contrário: "Parcial" quando `ehParcial=true`, senão "Integral"
  *
  * Resultado final (conceitual):
  *   "<prefixoEtapa><sufixoLocalidade><sufixoTurno>"
  *
  * Exemplos conceituais de saída:
  * - etapa=Creche, localidade=Urbano, turno=Parcial  -> "crecheUrbanaParcial"
  * - etapa=Pré-escola, localidade=Rural, turno=Integral -> "preEscolaRuralIntegral"
  * - etapa=EJA, localidade=Campo -> "ejaCampoParcial"
  */
  static obterNomePropriedadeBaseCaq(filtro: CategoriaFiltro): string {
    const tipoLocalizacaoFiltro = filtro.localidade.filtro;

    const localidade =
      tipoLocalizacaoFiltro === 'NENHUM'
        ? filtro.localidade.casoFiltroNenhum
        : tipoLocalizacaoFiltro;

    let sufixoTurno: 'Parcial' | 'Integral';

    if (filtro.etapa === 6) {
      sufixoTurno = 'Parcial';
    } else {

      if (filtro.ehParcial)
        sufixoTurno = 'Parcial';
       else
        sufixoTurno = 'Integral';

    }

    const sufixoLocalidade: Record<string, string> = {
      URBANO_SEM_FILTRO: 'UrbanaIndepLocDiferenciada',
      URBANO: 'Urbana',
      RURAL: 'Rural',
      CAMPO: 'Campo',
      TERRA_INDIGENA: 'TerraIndigena',
      QUILOMBOLA: 'Quilombola',
    };

    const prefixoEtapa: Record<number, string> = {
      1: 'creche',
      2: 'preEscola',
      3: 'ensFunAnosIniciais',
      4: 'ensFunAnosFinais',
      5: 'ensMedio',
      6: 'eja',
    };

    const prefixo = prefixoEtapa[filtro.etapa];

    const sufixoLoc = sufixoLocalidade[String(localidade)];

    if (!prefixo || !sufixoLoc) return '';

    return `${prefixo}${sufixoLoc}${sufixoTurno}`;
  }


  static obterNumeroEscolasCategorizada(
    dadosRelatorioCaq: DadosRelatorioCaqDto | null | undefined,
    filtro: CategoriaFiltro
  ): number{

      if (!dadosRelatorioCaq) return 0;

      const n = (v?: number | null):number => v ?? 0;

      try {
        const nomeBase = CaqUtils.obterNomePropriedadeBaseCaq(filtro);

        if (!nomeBase) return 0;

        const bloco = (dadosRelatorioCaq as any)[nomeBase];

        return n(bloco?.numeroEscolas);

      } catch (e) {
        console.warn('Erro ao acessar dados de escolas', filtro.etapa, filtro.ehParcial, e);
        return 0;
      }
  }

  static obterNumeroMatriculaCategorizado(
    dadosRelatorioCaq: DadosRelatorioCaqDto | null | undefined,
    filtro: CategoriaFiltro
  ): number {
    if (!dadosRelatorioCaq) return 0;

    const n = (v?: number | null) => v ?? 0;

    try {
      const nomeBase = CaqUtils.obterNomePropriedadeBaseCaq(filtro);
      if (!nomeBase) return 0;

      const bloco = (dadosRelatorioCaq as any)[nomeBase];
      return n(bloco?.numeroMatriculas);
    } catch (e) {
      console.warn('Erro ao acessar dados de matrículas', e);
      return 0;
    }
  }

  static obterValorCAQCategorizada(
    dadosRelatorioCaq: DadosRelatorioCaqDto | null | undefined,
    filtro: CategoriaFiltro,
    ehAno: boolean
  ): number {
    if (!dadosRelatorioCaq) {
      return 0;
    }

    let valorCaq = 0;

    const anual = (valor: number): number => (ehAno ? valor * 12 : valor);

    const n = (v?: number | null): number => v ?? 0;

    try {

      const nomeBase = CaqUtils.obterNomePropriedadeBaseCaq(filtro);

      if (!nomeBase) return 0;

      const bloco = (dadosRelatorioCaq as any)[nomeBase];

      valorCaq = anual(n(bloco?.mediana));
    } catch (e) {
      console.warn('Erro ao acessar dados do CAQ para etapa', filtro.etapa, filtro.ehParcial, e);
      return 0;
    }

    return valorCaq;
  }


  static faixasCaqMes() {
    // Valores fixos para mês: 800, 1200, 1600, 2000
    const valoresFixosMes = [800, 1200, 1600, 2000];

    return [
      { min: 0, max: valoresFixosMes[0] },
      { min: valoresFixosMes[0], max: valoresFixosMes[1] },
      { min: valoresFixosMes[1], max: valoresFixosMes[2] },
      { min: valoresFixosMes[2], max: valoresFixosMes[3] },
      { min: valoresFixosMes[3], max: Number.MAX_VALUE }
    ];
  }

  static calcularLarguraBarraCAQCategorizada(
    dadosRelatorioCaq: DadosRelatorioCaqDto | null,
    filtro: CategoriaFiltro,
    ehAno: boolean
  ): number {
    const larguraMaximaEmPx = 170;

    if (!dadosRelatorioCaq) return 0;

    const n = (v?: number | null) => v ?? 0;
    const anual = (v: number) => (ehAno ? v * 12 : v);

    try {

      const nomeBaseAtual = CaqUtils.obterNomePropriedadeBaseCaq(filtro);
      if (!nomeBaseAtual) return 0;

      const blocoAtual = (dadosRelatorioCaq as any)[nomeBaseAtual];

      const valorAtual = anual( n(blocoAtual?.mediana) );

      const etapas = [1, 2, 3, 4, 5, 6] as const;

      const localidades = ['URBANO_SEM_FILTRO', 'URBANO', 'RURAL', 'CAMPO', 'TERRA_INDIGENA', 'QUILOMBOLA'] as const;

      const turnos = ['Parcial', 'Integral'] as const;

      const valores: number[] = [];

      for (const etapa of etapas) {

        for (const localidade of localidades) {

          for (const turno of turnos) {

            if (etapa === 6 && turno === 'Integral') continue;

            const filtroMax: CategoriaFiltro = {
              etapa,
              ehParcial: turno === 'Parcial',
              localidade: {
                filtro: 'NENHUM',
                casoFiltroNenhum: localidade,
              },
            } as any;

            const nomeBase = CaqUtils.obterNomePropriedadeBaseCaq(filtroMax);

            if (!nomeBase) continue;

            const bloco = (dadosRelatorioCaq as any)[nomeBase];

            valores.push(
              anual( n(bloco?.mediana) )
            );

          }
        }
      }

      const valorMaximo = Math.max(...valores, 0);

      if (valorMaximo === 0 || valorAtual === 0) return 0;

      return Math.round((valorAtual / valorMaximo) * larguraMaximaEmPx);
    } catch (e) {
      console.warn('Erro ao calcular largura da barra CAQ:', e);
      return 0;
    }
  }
}
